import {
  CURRENCIES,
  CURRENCY_LABELS,
  DEFAULT_DISPLAY_CURRENCY,
  DEFAULT_USD_BRL_RATE,
  SUPPORTED_CURRENCIES,
} from "../constants/currency";
import { normalizePlatformName } from "./platformCatalog";

const SUPREMA_PLATFORM = "Suprema";

export const normalizeCurrency = (
  currency,
  fallback = DEFAULT_DISPLAY_CURRENCY
) => {
  const normalized = String(currency || "").trim().toUpperCase();
  return SUPPORTED_CURRENCIES.includes(normalized) ? normalized : fallback;
};

export const getPlatformCurrency = (platform) =>
  normalizePlatformName(platform).localeCompare(SUPREMA_PLATFORM, "pt-BR", {
    sensitivity: "base",
  }) === 0
    ? CURRENCIES.BRL
    : CURRENCIES.USD;

export const getCurrencyLabel = (currency) =>
  CURRENCY_LABELS[normalizeCurrency(currency)] || DEFAULT_DISPLAY_CURRENCY;

export const convertMoney = (
  amount,
  fromCurrency,
  toCurrency,
  usdBrlRate = DEFAULT_USD_BRL_RATE
) => {
  const value = Number(amount || 0);
  if (!Number.isFinite(value)) return 0;

  const from = normalizeCurrency(fromCurrency);
  const to = normalizeCurrency(toCurrency);
  const rate = Number(usdBrlRate);
  const safeRate = Number.isFinite(rate) && rate > 0 ? rate : DEFAULT_USD_BRL_RATE;

  if (from === to) return value;
  if (from === CURRENCIES.USD && to === CURRENCIES.BRL) return value * safeRate;
  if (from === CURRENCIES.BRL && to === CURRENCIES.USD) return value / safeRate;

  return value;
};

export const convertMoneyItem = (
  item,
  targetCurrency,
  usdBrlRate = DEFAULT_USD_BRL_RATE
) =>
  convertMoney(
    item?.amount,
    item?.currency,
    targetCurrency,
    usdBrlRate
  );

export const sumMoneyAmounts = (
  items = [],
  targetCurrency = DEFAULT_DISPLAY_CURRENCY,
  usdBrlRate = DEFAULT_USD_BRL_RATE
) =>
  items.reduce(
    (sum, item) => sum + convertMoneyItem(item, targetCurrency, usdBrlRate),
    0
  );

export const formatMoney = (
  amount,
  currency = DEFAULT_DISPLAY_CURRENCY,
  options = {}
) => {
  const n = Number(amount);
  const safeValue = Number.isFinite(n) ? n : 0;
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: normalizeCurrency(currency),
    ...options,
  }).format(safeValue);
};

export const resolveInitialCurrencyPreference = (
  platforms = [],
  currentPreference
) => {
  const current = normalizeCurrency(currentPreference, "");
  if (current) return current;

  const currencies = new Set(
    platforms
      .map((platform) => getPlatformCurrency(platform?.name || platform))
      .filter(Boolean)
  );

  if (currencies.size === 1) return [...currencies][0];
  return DEFAULT_DISPLAY_CURRENCY;
};

export const getMoneyContext = (options = {}) => ({
  displayCurrency: normalizeCurrency(
    options.displayCurrency,
    DEFAULT_DISPLAY_CURRENCY
  ),
  usdBrlRate:
    Number.isFinite(Number(options.usdBrlRate)) && Number(options.usdBrlRate) > 0
      ? Number(options.usdBrlRate)
      : DEFAULT_USD_BRL_RATE,
});

export const convertPlatformMoney = (
  amount,
  platform,
  targetCurrency,
  usdBrlRate = DEFAULT_USD_BRL_RATE,
  currency
) =>
  convertMoney(
    amount,
    normalizeCurrency(currency, getPlatformCurrency(platform)),
    targetCurrency,
    usdBrlRate
  );

